import {_, React, observer, computed, IDisposer, useEffect, classNames, useAsObservableSource, Fragment, stringify, pluralize, autorun, Popover2, utils} from '@api'
import {Menu, MenuDivider, MenuItem} from '@blueprintjs/core'
import {AssortmentCardStore, AssortmentCardStoreContext, CatalogFieldValueContextMenuItem, CombinedAssortmentHeroesDescriptor, CombinedHeroCardEntry, FILTER_UNASSIGNED_ID, HeroCardItemStore, HeroCardSecondLevelStore, HeroCardsSecondLevelStore, HIDDEN_HEROES_KEY, ICatalogItemDetailsPathElement, IContextMenuItems, IContextualHoverItemStore, LoadingIndicator, UNASSIGNED, useAssortmentCardStore, useVipLocalStore, VipLocalStore} from '@modules'
import {C8ImageEs, CatalogItemEs} from '@stores/elasticsearch'
import {removeUndefinedsNested} from '@utils'
import {DragEventPlaceholders, PrimaryIconButton} from 'centric-ui-components'
import {action, observable, runInAction, toJS} from 'mobx'
import {LegacyRef, Ref, RefCallback} from 'react'
import {AssortmentHeroCardItem, AssortmentHeroTitleCardStore, HeroCardTitle, HeroCardToRender, useAssortmentHeroCardsStore} from './'
import {DragDropEventHandlers, StoresInstance} from '@stores'
import * as css from './AssortmentHeroCard.css'
import {HeroCardsSecondLevel} from './HeroCardsSecondLevel'
import FlipMove from 'react-flip-move'

export const DEBUG_ASSORTMENTS = DEBUG && false

export type BomParentItemAny = any // Style in RH
export type BomChildrenItemAny = any;

/// region Store

export class AssortmentHeroCardStore extends VipLocalStore implements IContextMenuItems {
   constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { card: AssortmentCardStore, isCombinedHeroComponentStore?: boolean, parentCombinedHero?: AssortmentHeroCardStore }) {
    super(stores)
    this.selectedHeroIndex = _.toNumber(this.assortmentSettings.heroCards?.secondLevel?.selections?.[this.id]?.selected ?? 0)
    this.titleStore = new AssortmentHeroTitleCardStore(this)
  }

  @observable.ref secondLevel: HeroCardsSecondLevelStore

  get isCombinedHeroComponentStore() { return this.props.isCombinedHeroComponentStore === true}

  get parentCombinedHero() { return this.props.parentCombinedHero}

  titleStore: AssortmentHeroTitleCardStore = new AssortmentHeroTitleCardStore(this)

  @observable.shallow heroCardItemStores: Record<number, HeroCardItemStore> = {}

  @action toggleCustomSort = () => {
    const {dataKey, cardStore: {overrides}, hasSortOverrideDefinition} = this
    if (hasSortOverrideDefinition) {
      delete overrides.orders[dataKey]
      //overrides.orders[dataKey].enabled = overrides.orders[dataKey].enabled === false
    }
  }

  @computed get isSortOverridden() {
    const {dataKey, index, hasSortOverrideDefinition, cardStore: {overrides, keysToOriginalSortIndex}} = this
    const r = hasSortOverrideDefinition && overrides.orders[dataKey]?.enabled !== false

    return r && keysToOriginalSortIndex[dataKey] != overrides.orders[dataKey].index
  }

  @computed get hasSortOverrideDefinition() {
    const {dataKey, cardStore: {overrides}} = this
    return _.has(overrides?.orders, dataKey)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
        autorun(() => {
          const {assortmentSettings: {heroCards}, id, selectedHeroIndex: index} = this
          runInAction(() => {
            if (!heroCards.secondLevel?.selections) {
              _.merge(heroCards, {secondLevel: {selections: {}}})
            }
            if (heroCards.secondLevel.selections[id]?.selected !== (index ?? 0)) {
              _.merge(heroCards.secondLevel.selections, {[id]: {selected: index ?? 0}})
            }
          })
        }),
        autorun(() => {
          const {selectedSwatch, autoSelectHeroItem, bomTree, bomItemsFetched, filteredHeroItems, imagesForHeroItem} = this
          if (!_.isEmpty(bomTree) && !bomItemsFetched) return

          autoSelectHeroItem()
        }, {name: 'When selected swatch changes, try to find hero card items with images in that colorway to show'}),
        autorun(() => {
          const {bomTree} = this
          if (!_.isEmpty(bomTree)) {
            this.fetchBomItems()
          }
        })
      ],
      {props: {index, card}} = this
    card.componentStores.heroCards.cardStores[index] = this

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  autoSelectHeroItem = () => {
    const {filteredHeroItems, imagesForHeroItem} = this
    const indexToSelect = filteredHeroItems.findIndex(f => !_.isEmpty(imagesForHeroItem(f)))

    // console.log({title: this.titleStore.title, items: filteredHeroItems.slice().map(f => imagesForHeroItem(f)), indexToSelect})
    runInAction(() => {
      this.selectedHeroIndex = indexToSelect === -1 ? 0 : indexToSelect
      this.selectedSecondLevelItem = null
      if (this.selectedHeroItemStore) {
        this.selectedHeroItemStore.selectedImageIndex = 0
      }
    })
  }

  @computed get selectedHeroItemStore() {
    const {selectedHeroIndex, heroCardItemStores} = this
    return heroCardItemStores[selectedHeroIndex]
  }

  @action fetchBomItems = async () => {
    const {bomTree, catalog: {idFor}, es, catalog: {index}} = this

    const idsToFetch = _.flatMap(bomTree.map(({children}) => children.map(c => idFor(c, false))))
    if (!_.isEmpty(idsToFetch)) {
      this.isLoadingBOM = true
      const {items, total} = es.unwrapHitsResult(
        await es.search({
          index,
          size: idsToFetch.length,
          body: {
            query: {
              bool: {
                filter: {
                  terms: {
                    _id: idsToFetch
                  }
                }
              }
            }
          },
          meta: `Get BOM items for hero card ${this.id}`
        }))

      runInAction(() => {
        // console.log({idsToFetch, items, total})
        this.isLoadingBOM = false
        this.bomItemsFetched = true
        this.bomItemResults = items
        //console.log({idsToFetch, items, total})
      })
    }
  }

  @computed get hasBOMItems() { return !_.isEmpty(this.bomTree)}
  @observable isLoadingBOM = false
  @observable bomItemsFetched = false
  @observable bomItemResults: Array<any>

  @computed get bomItemById() {
     return _.keyBy(this.bomItemResults, v => v?.id)
  }

  imagesForHeroItem = (item) => {
    const {dataMap, es} = this,
      imageField = _.isString(dataMap.images) ? dataMap.images : dataMap.images?.field,
      images = imageField ? es.readField(item, imageField)?.val : null

    return images ?? []
  }

  get selectedSwatch() { return this.cardStore.card.selectedSwatchStore}

  get data() { return this.props.data}

  get cardStore() { return this.props.card.componentStores.heroCards}

  get assortment() { return this.cardStore.card}

  get config() { return this.cardStore.config}

  get dataMap() { return this.cardStore.dataMap}

  get id() {
    const {data, props: {index}} = this
    return data ? data.key.toString() : `empty-placeholder-${index}`
  }

  get assortmentSettings() {
    const {props: {card}} = this
    return card.assortmentCardSettings
  }

  @computed get dragDropId() {
    const {id, assortment: {id: assortmentId}} = this
    return `${assortmentId}-${id}`
  }

  @computed get dragDropEventHandlers(): DragDropEventHandlers {
    const {dragDropId} = this
    return new DragDropEventHandlers(this.catalog, {
      id: dragDropId, dragType: 'assortmentHeroCard', target: 'assortmentHeroCard', targetExtra: {originalIndex: this.props.index, hero: this}
    })
  }

  @computed get dataKey() {
    return this.props.data?.key?.toString() ?? UNASSIGNED
  }

  @computed get isCurrentDragItem() {
    return this.dragDropEventHandlers.isDragging
  }

  @computed get isFullWidth() {
    const {dataKey, assortmentSettings: cardSettings, assortment: {assortmentSettings}, row} = this,
      fullWidth = cardSettings?.heroCards?.overrides?.fullWidth
    return (fullWidth != null && fullWidth[dataKey] === true) || assortmentSettings?.hero?.fullWidthRows?.includes(row)
  }

  @observable dragOverState = {
    combine: false,
    reorder: false
  }

  @action onDragEnterCombineCards = () => {
    this.dragOverState.combine = true
  }
  @action onDragLeaveCombineCards = () => {
    this.dragOverState.combine = false
  }

  @action onDragEnterReorderCards = () => {
    this.dragOverState.reorder = true
    const {index, cardStore} = this
    // console.log(`onDragEnterReorderCards`, index) // autorun(() => {
    //   const {previewReorderCardIndex, dragDrop} = this
    //   if (previewReorderCardIndex) {
    //     if (!dragDrop.isDragging) {
    //       this.previewReorderCardIndex = null
    //     }
    //     else {
    //       console.log(`previewReorderCard:`, this.previewReorderCardIndex)
    //     }
    //   }
    // })
    cardStore._previewReorderCardsTimeout && clearTimeout(cardStore._previewReorderCardsTimeout)
    false && (cardStore._previewReorderCardsTimeout = window.setTimeout(() => {
      cardStore._previewReorderCardsTimeout = null
      // console.log(this, index)
      this.cardStore.previewReorderCardIndex = index
    }, 800))
  }

  get index() { return this.props.index}

  @action onDragLeaveReorderCards = () => {
    this.dragOverState.reorder = false
    const {cardStore} = this
    // console.log(`onDragExitReorderCards`, this.props.index)
    if (cardStore._previewReorderCardsTimeout) {
      window.clearTimeout(cardStore._previewReorderCardsTimeout)
      cardStore._previewReorderCardsTimeout = null
      // if (this.cardStore.previewReorderCardIndex === this.props.index) ~{
      //   this.cardStore.previewReorderCardIndex = null
      // }
    }
  }

  @computed get row() {
    const {cardStore: {cardsToRender}, assortment: {root, assortmentSettings: {layout: {columns}, hero: {fullWidthRows}}}} = this

    let row = 0, index = this.index
    do {
      if (index < 0) {
        return row - 1
      } else if (index === 0) {
        return row
      } else if (fullWidthRows.includes(row)) {
        index--
        row++
      } else {
        index -= (columns - index % columns)
        row++
      }
    } while (true)
  }

  @computed get cssStyles() {
    const {dragDropEventHandlers: {isDragging}, row, assortment: {root, assortmentSettings: {layout: {columns}}}, isFullWidth} = this

    return {
      '--combined-hero-count': 1 + (this.data?.combined ?? []).length,
      '--hero-row': row,
      '--column-span': isFullWidth ? columns : 1,
      ...(isDragging ? root.styles : {})
    }
  }

  @observable.ref rootDiv?: HTMLDivElement
  onSetRef = (ref: RefCallback<HTMLDivElement>) => (d) => {
    this.rootDiv = d
    ref && ref(d)
  }

  @action onCustomizeDragImageNode = (div: HTMLDivElement) => {
    // We require our -- values to render the element in the correct custom size
    const {dragDrop: {dragItemClonedNode}, assortment: {root: {styles}}} = this
    _.entries(styles).forEach(([property, v]) => {
      dragItemClonedNode && dragItemClonedNode.style.setProperty(property, v as any)
    })
  }
  @action onDragStart = (e: React.DragEvent) => {
    const {rootDiv, onCustomizeDragImageNode} = this
    // console.log(rootDiv)
    this.dragDropEventHandlers.onDragStart(e, {setDragImage: true, elementToClone: rootDiv, onCustomizeDragImageNode})
  }
  // @computed get dragDropEventHandlers() {
  //   return this.cardStore.dragPlaceholderFor(this)
  // }

  @action onDropCombineCards = (e: React.DragEvent) => {
    e.stopPropagation()
    e.preventDefault()

    const {cardStore: {overrides: {combinedShapes, orders}}, dragDrop: {dragItem, dragOverItem}} = this,
      dragHero = dragItem.targetExtra.hero as AssortmentHeroCardStore,
      dropHero = dragOverItem.targetExtra.hero as AssortmentHeroCardStore,
      {index: dragIndex} = dragHero, {index: dropIndex} = dropHero
    DEBUG_ASSORTMENTS && console.log(`onDropCombineCards`, {e, dragItem, dragHero, dropHero})

    // Already a combo using this key?
    const existingCombo = combinedShapes.find(s => s.keys.includes(dropHero.dataKey))
    if (existingCombo) {
      if (existingCombo.keys.includes(dragHero.dataKey)) return

      existingCombo.keys.push(dragHero.dataKey)
    } else {
      combinedShapes.push({keys: [dropHero.data.key?.toString(), dragHero.data.key?.toString()], orientation: 'horizontal'})
    }

    if (dropHero.index == dragHero.index + 1) {
      orders[dropHero.dataKey] = {index: dragIndex}
    }

    // Anyone custom sorted to the right of us needs to shift left one
    _.keys(orders).forEach(k => {
      if (orders[k].index > dragIndex) {
        orders[k].index--
      }
    })
  }

  @action onDropReorderCards = (e: React.DragEvent) => {
    e.stopPropagation()
    e.preventDefault()

    const {cardStore, cardStore: {overrides: {orders}, cardsInOriginalOrderAndCombined, keysToOriginalSortIndex}, dragDrop: {dragItem, dragOverItem}} = this,
      dragHero = dragItem.targetExtra.hero as AssortmentHeroCardStore,
      dropHero = dragOverItem.targetExtra.hero as AssortmentHeroCardStore,
      currentCards = cardStore.cardsInCustomOrder.slice()

    DEBUG_ASSORTMENTS && console.log(`onDropReorderCards`, {cardsInOriginalOrderAndCombined, dragItem, dragHero, dropHero, currentOrders: toJS(orders)})

    const dropIndex = dropHero.props.index

    if (orders) {
      const {dataKey: dragKey} = dragHero
      if (orders[dragKey]) {
        delete orders[dragKey]
      }

      orders[dragKey] = {index: dropIndex, enabled: true}

      DEBUG_ASSORTMENTS && console.debug(`D&D - move ${dragKey} from ${dragHero.index} -> to ${orders[dragKey].index}`)

      // Add custom sort markers for everyone impacted by this move (everyone to my right)
      for (let i = dropIndex; i < currentCards.length; i++) {
        if (i != dragHero.index) {
          orders[currentCards[i].key?.toString() ?? UNASSIGNED] = {index: i <= dragHero.index ? i + 1 : i, enabled: true}
        }
      }

      // Remove anyone who has ended up at their original order index
      // _.keys(orders).forEach(k => {
      //   if (keysToOriginalSortIndex[k] === orders[k].index) {
      //     delete orders[k]
      //   }
      // })
    }
    // const positionToPlace =
  }

  /**
   * The 'skeleton' of the hero box that can be filled in with the selected style + color combination.  Shape Master in RH
   */
  @computed get detailsPathSkeleton(): ICatalogItemDetailsPathElement[] {
    const {
      cardStore: {foreignKeyFieldSkeleton},
      catalog, catalog: {idFor}, selectedHeroItem, firstItem
    } = this
    if (!selectedHeroItem || foreignKeyFieldSkeleton === undefined) return []

    let data = firstItem?.__parent ?? firstItem

    const r = [
      removeUndefinedsNested({
        where: [{value: idFor(foreignKeyFieldSkeleton?.esIndex ? selectedHeroItem : data, false)}],
        esIndex: foreignKeyFieldSkeleton?.esIndex ?? catalog?.index,
        catalogField: foreignKeyFieldSkeleton?.esIndex
      })
    ]

    return r
  }

  /**
   * The actively shown colorway of some style/color in the RH prototype
   */
  @computed get detailsPathRealized(): ICatalogItemDetailsPathElement[] {
    const {
      cardStore: {foreignKeyFieldRealized}
      , catalog, catalog: {idFor}, selectedHeroItem
    } = this
    if (!selectedHeroItem || foreignKeyFieldRealized === undefined) return []

    return [
      {
        where: [{value: idFor(selectedHeroItem, false)}],
        esIndex: foreignKeyFieldRealized?.esIndex ?? catalog?.index,
        catalogField: foreignKeyFieldRealized?.field
      }
    ]
  }

  @computed get createHeroCardItemTooltip() {
    const {catalog, associatedHeroItemField} = this
    return associatedHeroItemField ? `Create New ${pluralize(catalog.labelForField(associatedHeroItemField), 1)}` : ''
  }

  @action onCreateHeroCardItem = () => {
    console.log(`NYI - RH 1.1`)
  }

  /**
   * The selected items filtered by the users toggled swatches/2nd level items
   */
  @computed get filteredHeroItems() {
    const {heroItems, cardStore: {card: {selectedSwatchStore}}} = this

    let r = heroItems
    if (selectedSwatchStore?.config) {
      const {config: {itemField}, id} = selectedSwatchStore
      r = heroItems.filter(item => _.get(item, itemField) === id)
    }

    return r
  }

  @computed get selectedItems() {
    const {filteredHeroItems, selectedSecondLevelItem, config: {child: childConfig}} = this
    if (!selectedSecondLevelItem || !childConfig) return filteredHeroItems

    return filteredHeroItems.filter(f => selectedSecondLevelItem.matchesHeroItem(f))
  }

  @computed get bomTree() : Array<{item: BomParentItemAny, children: Array<BomChildrenItemAny>}> {
    const {data, config, es} = this
    if (config?.child?.bom?.field && data?.items) {
      const {child: {bom}} = config
      let items = data.items

      if (bom.mustHaveField) {
        items = items.filter((v: any) => _.get(v, bom.mustHaveField))
      }

      let r = items.map(v => {
        let children = es.readField(v, bom.field)?.val ?? []

        // This won't work as we only have limited data in the parent's list of child BOMs.  We have to load the real child style and then apply the filter there
        // if (bom.onlyIfField) {
        //   children = children.filter(c => _.get(c, bom.onlyIfField))
        // }
        return ({item: v, children})
      })

      if (!_.isEmpty(items)) {
        r = r.filter(v => v.children.length > 0)
      }

      return r
    }

    return []
  }

  /**
   * The selected items filtered by the users toggled swatches/2nd level items
   */
  @computed get heroItemsRaw() {
    const {data, config, es, bomTree, bomItemById} = this
    let r = []

    if (!_.isEmpty(bomTree)) {
      r = bomTree.map(v => {
        let children = v.children.map(c => bomItemById[c.id]).filter(_.identity)

        if (config.child?.bom.onlyIfField) {
          children = children.filter(v => _.get(v, config.child.bom.onlyIfField))
        }

        return [/*!_.isEmpty(children) ? v.item : null*/ v.item, ...children].filter(_.identity)
      }).flat()
    }
    else {
      r = data?.items ?? []
    }

    if (!config?.item?.field) { return r}
    if (config?.item?.field) {
      r = _.flatMap((r ?? []).map(v => es.readField(v, config.item.field)?.val))
    }

    return r
  }

  /**
   * BOM Items are not found by our normal query to pick elements for the assortment card, but are instead set as some property on the item in a parent -> child relationship that we need to pull from ES
   * after the fact
   */
  @observable bomItems = observable.map<string, any>({}, {deep: false})

  @computed get heroItems() {
    const {heroItemsRaw: heroItems, config, stores} = this

    let r = [...(heroItems ?? [])]

    return config?.sort ? config.sort(r, stores) : r
  }

  /**
   * There is not always a hero card to select at a given clicked item + swatch selection (we show the +).  This is to show the underline/selection state for the 2nd level item
   */
  @observable.ref selectedSecondLevelItem: HeroCardSecondLevelStore

  @observable selectedHeroIndex: number = 0

  @computed get firstItem() {
    return _.first(this.data?.items ?? [])
  }

  @computed get selectedHeroItem() {
    const {filteredHeroItems, selectedHeroIndex} = this
    return selectedHeroIndex > filteredHeroItems.length - 1 ? _.first(filteredHeroItems) : filteredHeroItems[selectedHeroIndex]
  }

  @computed get placeholderImg() {
    const {selectedHeroItem} = this

    //DEBUG_ASSORTMENTS && console.log(`placeholderImg`, selectedHeroItem)
    return null // !this.data ? null : '/images/assortment/placeholder-hero-overlay.png'
  }

  // get rawItems() : Array<CatalogItemEs> {
  //   return []
  // }

  @computed get keys() {
    const {config: {field, child}} = this

    return [
      field ?? '_id',
      child ? child.field ?? '_id'
            : null
    ].filter(_.identity)
  }

  /**
   * Hero cards are found at an intersection of 2nd level card choice + swatch choice(s)
   *
   * For example, in RH:
   *    1.  A specific theme master + cat4 selects some ## of styles
   *    2.  Those styles are then divided into hero card by Shape Master <-- you are here
   *    3.  The displayed hero card is then the selected style + color -> colorway image
   *      If no style is selected, use the first style in the list
   *      If no color is selected use the first colorway in the selected style's list
   */
  @computed get heroLookups(): { [keys: string]: HeroCardCacheItem } {
    if (!this.data) return {}

    const {data: {items}} = this

    const wrappers = items.map((item, i) => new HeroCardCacheItem(this, item, i))
    return _.keyBy(wrappers, w => stringify(w.keys))
  }

  @computed get foreignKeyValueSkeleton() {
    const {es, cardStore: {foreignKeyFieldSkeleton}, contextMenu: cm} = this
    const item = cm.extra.item ?? this.selectedHeroItem

    if (!foreignKeyFieldSkeleton || !item) return null

    if (!foreignKeyFieldSkeleton?.esIndex) {}
    const v = !foreignKeyFieldSkeleton?.esIndex ? (item?.__parent ?? item) : es.readField(item?.__parent ?? item, foreignKeyFieldSkeleton.field)?.val
    return _.isArray(v) ? _.first(v) : v
  }

  @computed get heroCardField() {
    const {catalog, config: {item}} = this
    if (item?.field) return catalog._fieldByName[item.field]
    return null
  }

  get contextMenuItems() {
    const {
      cardStore: {foreignKeyFieldSkeleton}, foreignKeyValueSkeleton, heroCardField, selectedHeroItem, selectedSecondLevelItem,
      catalog: {idFor}
    } = this

    return <AssortmentCardStoreContext.Provider value={this.assortment}>
      <AssortmentHeroCardStoreContext.Provider value={this}>
        {selectedHeroItem && heroCardField && <CatalogFieldValueContextMenuItem value={selectedHeroItem} field={heroCardField}/>}
        {selectedSecondLevelItem && idFor(selectedSecondLevelItem.item, false) != idFor(selectedHeroItem, false) && selectedSecondLevelItem?.fieldValueContextMenuItem}
        {foreignKeyFieldSkeleton && <CatalogFieldValueContextMenuItem field={foreignKeyFieldSkeleton} value={foreignKeyValueSkeleton}/>}
        <MenuDivider/>

        {this.cardStore.card.contextMenuItems}
      </AssortmentHeroCardStoreContext.Provider>
    </AssortmentCardStoreContext.Provider>
  }

  @action onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  get catalogFieldSkeleton() { return this.cardStore.catalogFieldSkeleton}

  /// region Filters

  @computed get filterGroupKey() {
    return this.config.field
  }

  @computed get filterValueRaw() {
    const {data, catalogFieldSkeleton, filter: {filterFieldFor}} = this
    if (!data || !catalogFieldSkeleton) return undefined

    return _.get(_.first(data.items), filterFieldFor(catalogFieldSkeleton))
  }

  @computed get filterValue() {
    const {data, catalogFieldSkeleton, catalog: {idFor}, filterValueRaw} = this
    if (!data || !catalogFieldSkeleton) return undefined

    if (filterValueRaw === null) return FILTER_UNASSIGNED_ID

    return catalogFieldSkeleton.esIndex ? idFor(filterValueRaw) : filterValueRaw
  }

  @computed get filterValueAsText() {
    const {filter, filterGroupKey, filterValue} = this
    return filter.labelFor({group: filterGroupKey, value: filterValue})
  }

  @computed get filterGroup() {
    const {filter, filterGroupKey} = this,
      r = filter.groupFor(filterGroupKey)

    return _.isEmpty(r) ? null : r
  }

  @computed get isFilterSelected() {
    if (!this.filterGroup) return false
    const {filterGroup: {selected}, filterValue} = this
    return selected?.has(filterValue)
  }

  @computed get isFilterExcluded() {
    if (!this.filterGroup) return false
    const {filterGroup: {excluded}, filterValue} = this
    return excluded?.has(filterValue)
  }

  @action toggleFilterSelected = () => {
    const {filterGroupKey, filter, filterValue} = this
    filter.toggleFilter({key: filterGroupKey, value: filterValue})
  }

  @action toggleFilterExcluded = () => {
    const {filterGroupKey, filter, filterValue} = this
    filter.toggleFilter({key: filterGroupKey, exclude: true, value: filterValue})
  }

  /// endregion

  @computed get associatedWorkspaceSkeleton() {
    const {catalog, cardStore: {foreignKeyFieldSkeleton}} = this
    return foreignKeyFieldSkeleton ? catalog.workspaceWrapperForField(foreignKeyFieldSkeleton) : catalog.workspace
  }

  @computed get associatedWorkspaceRealized() {
    const {catalog, cardStore: {foreignKeyFieldRealized}} = this
    return foreignKeyFieldRealized ? catalog.workspaceWrapperForField(foreignKeyFieldRealized) : catalog.workspace
  }

  @computed get associatedHeroItemField() {
    const {catalog, config: {item}} = this
    if (!item?.field) return null

    const f = catalog._fieldByName[item.field]

    return f
  }

  @computed get associatedHeroItemWorkspace() {
    const {associatedHeroItemField, catalog} = this
    return (associatedHeroItemField ? catalog.associatedWorkspaceFor(associatedHeroItemField) : null) ?? catalog.workspace
  }

  /**
   * Generally should be opening a 'colorway' for the RH case (hero box is a shape master, current image is some specific colorway)
   * @param e
   */
  onDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    const {
      stores: {ui, flexLayoutCatalogRoot: {tab}},
      assortment, detailsPathRealized, associatedWorkspaceRealized
    } = this

    !_.isEmpty(detailsPathRealized) && associatedWorkspaceRealized && tab.openCatalogItemDetailsInNewTab({
      path: detailsPathRealized,
      workspace: associatedWorkspaceRealized._id,
      parentTab: tab.id
    })

    setTimeout(() => assortment.scrollIntoView(), ui.settings.flexLayout.scrollIntoViewDelayAfterNewChildTab)
  }

  @computed get selectedHeroItemRootId() {
    const {catalog: {idFor}, selectedHeroItem} = this
    return idFor(selectedHeroItem?.__parent, false)
  }

  @computed get disablePreviousHeroCard() {
    return _.isEmpty(this.filteredHeroItems)
  }

  @computed get disableNextHeroCard() {
    return _.isEmpty(this.filteredHeroItems)
  }

  @action onPreviousHeroCard = () => {
    const {selectedHeroIndex, heroCardItemStores, filteredHeroItems} = this,
      selectedHeroItem = heroCardItemStores[selectedHeroIndex]

    if (selectedHeroItem && selectedHeroItem.selectedImageIndex > 0) {
      selectedHeroItem._selectedImageIndex--
    } else {
      this.selectedSecondLevelItem = null
      this.selectedHeroIndex = selectedHeroIndex > 0 ? selectedHeroIndex - 1 : filteredHeroItems.length - 1

      const store = heroCardItemStores[this.selectedHeroIndex]
      if (store) {
        store.selectedImageIndex = store.imageUrls.length - 1
      }
    }
  }

  @action onNextHeroCard = () => {
    const {selectedHeroIndex, heroCardItemStores, filteredHeroItems} = this,
      selectedHeroItem = heroCardItemStores[selectedHeroIndex]

    if (selectedHeroItem && selectedHeroItem.hasMoreImages) {
      selectedHeroItem._selectedImageIndex = selectedHeroItem.selectedImageIndex + 1
    } else {
      this.selectedSecondLevelItem = null
      this.selectedHeroIndex = selectedHeroIndex + 1 < filteredHeroItems.length ? selectedHeroIndex + 1 : 0

      const store = heroCardItemStores[this.selectedHeroIndex]
      if (store) {
        store.selectedImageIndex = 0
      }
    }
  }

  @computed get isCombinedWithOtherHeroes() {
    return !_.isEmpty(this.data?.combined)
  }

  @action flipCombinedOrientation = () => {
    const {parentCombinedHero, combinedSettingsEntry} = this

    if (parentCombinedHero) {
      parentCombinedHero.flipCombinedOrientation()
    } else {
      combinedSettingsEntry.orientation = combinedSettingsEntry.orientation === 'horizontal' ? 'vertical' : 'horizontal'
    }
  }

  @computed get combinedSettingsEntry(): CombinedAssortmentHeroesDescriptor {
    return (this.cardStore?.overrides?.combinedShapes ?? []).find(o => o.keys.includes(this.dataKey))
  }

  @computed get combinedCount() {
    return this.combinedSettingsEntry?.keys?.length ?? 0
  }

  @action onUncombineHero = () => {
    const {combinedSettingsEntry, index, onUncombineHeroKey, dataKey, cardStore: {overrides, insertCustomSortedHeroAtIndex}} = this

    if (combinedSettingsEntry && combinedSettingsEntry.keys.length <= 1) {
      overrides.orders[dataKey] = {index} // So we don't move
      overrides.combinedShapes = overrides.combinedShapes.filter(v => v != combinedSettingsEntry)
    } else {
      _.reverse(combinedSettingsEntry.keys) // drop items from right to left
        .forEach(k => {
          if (k != dataKey) {
            onUncombineHeroKey(k)
          }
        })
    }

    console.log(overrides.orders)
  }

  @action onUncombineHeroKey = (key: string) => {
    const {combinedSettingsEntry, cardStore: {insertCustomSortedHeroAtIndex}} = this

    DEBUG_ASSORTMENTS && console.log(`onUncombineHeroKey`, {key, combinedSettingsEntry: toJS(combinedSettingsEntry)})

    if (combinedSettingsEntry) {
      combinedSettingsEntry.keys = combinedSettingsEntry.keys.filter(v => v != key)

      if (key != this.dataKey) {
        insertCustomSortedHeroAtIndex(key, this.index + 1)
      }

      if (combinedSettingsEntry.keys.length <= 1) {
        this.onUncombineHero()
      }
    }
  }

  @action onUncombineAll = () => {
    const {cardStore: {overrides}} = this

    overrides.combinedShapes = []
  }

  @action toggleFullWidth = () => {
    const {parentCombinedHero, dataKey, cardStore: {overrides}} = this
    if (parentCombinedHero) {
      parentCombinedHero.toggleFullWidth()
    } else if (!overrides.fullWidth) {
      overrides.fullWidth = {[dataKey]: true}
    } else {
      const {fullWidth} = overrides
      if (fullWidth[dataKey]) {
        delete fullWidth[dataKey]
      } else {
        fullWidth[dataKey] = true
      }
    }
  }

  @computed get isReallySubdivided() {
    return (this.isCombinedWithOtherHeroes && this.subdivideCards) || this.parentCombinedHero?.isReallySubdivided
  }

  @computed get subdivideCards() {
    const {dataKey, cardStore: {overrides}} = this
    return overrides?.subdivide?.[dataKey] === true
  }

  @action toggleSubdivide = () => {
    const {parentCombinedHero, dataKey, cardStore: {overrides}} = this
    if (parentCombinedHero) {
      parentCombinedHero.toggleSubdivide()
    } else if (this.subdivideCards) {
      delete overrides.subdivide[dataKey]
    } else {
      _.merge(overrides, {subdivide: {[dataKey]: true}})
    }
  }

  @action onCopyStoreToWindow = () => {
    window['assortmentCardHero'] = this
    DEBUG_ASSORTMENTS && console.debug(`Copied store for '${this.titleStore.title}' into window.assortmentCardHero and your clipboard`)
    navigator.clipboard.writeText(`window.assortmentCardHero`)
  }
}

export const AssortmentHeroCardStoreContext = React.createContext<AssortmentHeroCardStore>(null)
export const useAssortmentHeroCardStore = () => React.useContext(AssortmentHeroCardStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  data?: HeroCardToRender
  index: number
}

export const AssortmentHeroCard = observer<MyProps, HTMLDivElement>(function AssortmentHeroCard({className, index, ...props}, ref) {
  const card = useAssortmentCardStore(),
    obsProps = useAsObservableSource({data: props.data, index, card}),
    store = useVipLocalStore((stores) => new AssortmentHeroCardStore(stores, obsProps)),
    {
      filteredHeroItems, dragDropId, combinedSettingsEntry, data, onContextMenu, hasBOMItems, isLoadingBOM,
      isFullWidth, isReallySubdivided, id, dragDropEventHandlers, cssStyles, isCurrentDragItem
    } = store,
    {componentStores: {heroCards: {mouseEventsFor}}} = card

  useEffect(() => store.registerEffects(), [])

  // if (!_.isEmpty(bom)) {
  //   console.log(toJS({bom, data, id}))
  // }
  //console.log(toJS(heroLookups))

  return (
    <AssortmentHeroCardStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           ref={store.onSetRef(ref as RefCallback<HTMLDivElement>)}
           data-type='HeroCard'
           data-index={index}
           data-id={id}
           data-full-width={isFullWidth ? true : undefined}
           data-orientation={combinedSettingsEntry?.orientation}
           data-subdivide={isReallySubdivided ? true : undefined}
           data-items={filteredHeroItems.length}
           data-dragging={isCurrentDragItem}
           data-show-drop-placeholder={id && dragDropEventHandlers.showDropIndicator}
           style={cssStyles as any}
        //data-matches-hover-item={matchesContextualCatalogItem(item, field.field)}
           {...mouseEventsFor(store)}
           {..._.omit(dragDropEventHandlers.events, ['onDragStart'])}
           draggable
           onDragStart={store.onDragStart}
           onDoubleClick={utils.eatMouseEvent}
           onContextMenu={onContextMenu}
      >

        {hasBOMItems && isLoadingBOM ? <LoadingIndicator /> :<Fragment>
        {/*{data?.combined ? <CombinedHeroesFloatingToolbar /> : null}*/}
        {/*{imageBackgroundUrl && <img className={css.backgroundImage} src={imageBackgroundUrl} />}*/}

        {store.dataKey === HIDDEN_HEROES_KEY
         ? <HeroCardsFilteredInfo/>
         : <Fragment>
           {data && <Fragment>
             {!isReallySubdivided ? <HeroCardTitle key={'title'}/> : null}

			   <DragEventPlaceholders
				   className={css.dropContainer}
				   dropType='assortmentHeroCard'
				   id={dragDropId}
			   >
				   <AssortmentHeroCardDropPlaceholders/>
			   </DragEventPlaceholders>

			   <FlipMove className={classNames(css.heroCardContents)} enterAnimation='fade' leaveAnimation='fade'>
				   <HeroCardContents key={store.dataKey}/>
                 {(data?.combined ?? []).map((v, i) => <HeroCardContentsCombinedEntry index={i + 1} key={`${v.key}-${combinedSettingsEntry?.orientation}`} entry={v}/>)}
			   </FlipMove>
		   </Fragment>
           }
         </Fragment>}
        </Fragment>}
      </div>
    </AssortmentHeroCardStoreContext.Provider>)
}, {forwardRef: true})

export const HeroCardsFilteredInfo = observer(function HeroCardsFilteredInfo() {
  const {hiddenHeroes, showHiddenHeroes} = useAssortmentHeroCardsStore()

  return (<div className={css.heroesFiltered} onClick={showHiddenHeroes}>
    ...and {hiddenHeroes.length.toLocaleString()} more
  </div>)
})
export const HeroCardContentsCombinedEntry = observer<{ entry: CombinedHeroCardEntry, index: number }, HTMLDivElement>(function HeroCardContentsCombinedEntry({entry, index}, ref) {
  const card = useAssortmentCardStore(),
    parentStore = useAssortmentHeroCardStore(),
    obsProps = useAsObservableSource({data: entry, index, card, isCombinedHeroComponentStore: true, parentCombinedHero: parentStore}),
    store = useVipLocalStore((stores) => new AssortmentHeroCardStore(stores, obsProps))

  useEffect(() => store.registerEffects(), [])
  return (
    <AssortmentHeroCardStoreContext.Provider value={store}>
      <HeroCardContents ref={ref as any}/>
    </AssortmentHeroCardStoreContext.Provider>
  )
}, {forwardRef: true})

export const HeroCardContents = observer<{}, HTMLDivElement>(function HeroCardContents({}, ref) {
  const store = useAssortmentHeroCardStore()

  const {data, isReallySubdivided, filteredHeroItems: items, selectedItems, selectedHeroIndex, selectedHeroItem} = store,
    heroKey = store.dataKey

  return (<div ref={ref} className={css.heroContentInternal} data-type='HeroCardContentInternal'>
    {isReallySubdivided && <HeroCardTitle key={'title'}/>}

    <FlipMove typeName={null} leaveAnimation='elevator' appearAnimation='fade'>
      <div key={'heroContents'} className={css.internalContainer}>
        {!_.isEmpty(items) && <Fragment>
			<PreviousHeroCardItemIconButton/>
			<NextHeroCardItemIconButton/>
		</Fragment>}

        {_.isEmpty(selectedItems)
         ? (data ? <AddNewHeroCardItem/> : null)
         : false ? <AssortmentHeroCardItem heroKey={heroKey} item={selectedHeroItem} index={selectedHeroIndex}/>
                 : items.map((item, i) => <AssortmentHeroCardItem heroKey={heroKey} item={item} index={i} key={i}/>)}
      </div>
      <HeroCardsSecondLevel key='secondlevel' heroKey={heroKey} items={items} className={css.secondLevelCards}/>
    </FlipMove>
  </div>)
}, {forwardRef: true})

export const AddNewHeroCardItem = observer(function AddNewHeroCardItem() {
  const {createHeroCardItemTooltip, onCreateHeroCardItem} = useAssortmentHeroCardStore()

  return (<Fragment>
    <div className={css.noHeroCardItem}>
      <PrimaryIconButton className={css.addHeroCardItem}
                         icon='plus'
                         onClick={onCreateHeroCardItem}
                         disabled
                         tooltipPosition='bottom'
                         tooltip={createHeroCardItemTooltip}/>
    </div>
  </Fragment>)
})

export const PreviousHeroCardItemIconButton = observer(function PreviousHeroCardItemIconButton() {
  const {disablePreviousHeroCard, onPreviousHeroCard} = useAssortmentHeroCardStore()

  return (<PrimaryIconButton disabled={disablePreviousHeroCard}
                             tooltipPosition='bottom'
                             tooltip='Previous'
                             icon='chevron-left'
                             wrapperClassName={css.previousHeroCard}
                             onClick={() => onPreviousHeroCard()}/>)
})

export const NextHeroCardItemIconButton = observer(function PreviousHeroCardItemIconButton() {
  const {disableNextHeroCard, onNextHeroCard} = useAssortmentHeroCardStore()

  return (<PrimaryIconButton disabled={disableNextHeroCard}
                             tooltipPosition='bottom'
                             tooltip='Next'
                             icon='chevron-right'
                             wrapperClassName={css.nextHeroCard}
                             onClick={() => onNextHeroCard()}/>)
})

export const PlaceholderHeroCardImage = observer(function PlaceholderHeroCardImage() {
  const {placeholderImg} = useAssortmentHeroCardStore()

  return (!placeholderImg ? <div className={css.noHeroCardImage}/>
                          : <img className={css.placeholderImg} src={placeholderImg}/>)
})

export class HeroCardCacheItem {
  constructor(public card: AssortmentHeroCardStore, public data: CatalogItemEs, public indexInParent: number) {}

  get catalog() { return this.card.catalog}

  @computed get keys() {
    const {card: {keys}, data} = this

    return keys.reduce((acc, k) => ({...acc, [k]: _.get(data, k)}), {})
  }

  @computed get images(): Array<C8ImageEs> {
    const {card: {dataMap}, data} = this
    return _.get(data, dataMap.images as string)
  }
}

export const AssortmentHeroCardDropPlaceholders = observer(function AssortmentHeroCardDropPlaceholders() {
  const {dragDropEventHandlers, onDragEnterCombineCards, onDragLeaveCombineCards, dragOverState, onDragEnterReorderCards, onDragLeaveReorderCards, onDropCombineCards, onDropReorderCards} = useAssortmentHeroCardStore()

  return (<div className={css.dropPlaceholder}
               {...dragDropEventHandlers.events}
               onDrop={e => {
                 e.preventDefault()
                 e.stopPropagation()
               }}
  >
    <div className={css.action} data-drag-over={dragOverState.combine} onDragEnter={onDragEnterCombineCards} onDragLeave={onDragLeaveCombineCards} onDrop={onDropCombineCards}>Combine Cards</div>
    <div className={css.action} data-drag-over={dragOverState.reorder} onDragEnter={onDragEnterReorderCards} onDragLeave={onDragLeaveReorderCards} onDrop={onDropReorderCards}>Reorder Cards</div>
  </div>)
})

export const CombinedHeroesFloatingToolbar = observer(function CombinedHeroesFloatingToolbar() {
  const {} = useAssortmentHeroCardStore()

  return (<div className={css.combinedHeroesToolbar}>
    <Popover2
      interactionKind='hover'
      placement='bottom'
      content={
        <Menu>
          <MenuDivider/>
          <MenuItem icon='split-columns' text='Split'/>
        </Menu>
      }
    >
      <PrimaryIconButton icon='menu'/>
    </Popover2>
  </div>)
})
/// endregion



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/HeroCards/AssortmentHeroCard.tsx